import React, { useContext } from "react";
import { useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import clsx from "clsx";
import Alert from "@mui/material/Alert";

// Custom.
import { regexPatterns } from "@vocollege/app/dist/modules/VoHelpers";
import VoAuth from "@vocollege/app/dist/modules/VoAuth";
import Layout from "containers/Layout/Layout";
import Form from "@vocollege/components/dist/Form";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  GET_USER,
  UPDATE_USER,
  CREATE_USER,
  CREATE_FILE,
  UPDATE_FILE,
  DELETE_FILE,
} from "@vocollege/app/dist/modules/VoApi/graphql";
import Utils from "services/Utils";
import { FormTabProps } from "@vocollege/components/dist/Form/global";
import { useStyles } from "./styles";
import { UserEditProps } from "./global";
import AccountAccess, { ACCOUNT_ACCESS } from "./AccountAccess";
// import VoGroups from "@vocollege/app/dist/modules/VoGroups";
// import { GeneralObject } from "@vocollege/app/dist/global";
import { VoAppContext } from "services/GlobalState";

export const initialState: any = {
  id: 0,
  email: "",
  name: "",
  firstname: "",
  lastname: "",
  active: true,
  images: [],
  personal_number: "",
  address1: "",
  address2: "",
  postalcode: "",
  town: "",
  country: "",
  phone1: "",
  phone2: "",
  profession: "",
  status: 0,
  notify: false,
  gdpr: false,
  workplace: "",
  groups: [],
  roles: [],
  groupRoles: [],
  apps: [],
  settings: [],
};

const CATEGORY = "user";

const UserEdit: React.FC<UserEditProps> = (props) => {
  const { dialog, initialData, urlParams, onComplete, onCancel } = props;
  const params = useParams<any>();
  const navigate = useNavigate();
  const classes = useStyles();
  const { state } = useContext(VoAppContext);

  const isCreateNew = () => {
    if (urlParams) {
      return urlParams.id === "create";
    }
    return params.id === "create";
  };

  const accountId = String(urlParams?.id || params.id);
  const canManageAccess =
    !state.group &&
    !isCreateNew() &&
    accountId !== String(VoAuth.currentUser.id) &&
    VoAuth.ability.can("update", "Users");
  const { data: accountAccess, loading: accessLoading, error: accessError } = useQuery(ACCOUNT_ACCESS, {
    variables: { id: accountId },
    skip: !canManageAccess,
    fetchPolicy: "network-only",
  });

  const getInitialState = () => {
    const newInitialState = { ...initialState };
    if (isCreateNew() && state.group) {
      newInitialState.groups = [
        {
          id: state.group.id,
          title: state.group.title,
          type: state.group.type,
        },
      ];
    }
    return { ...newInitialState, ...(initialData || {}) };
  };

  // const getInitialData = () => {
  //   if (!initialData) {
  //     return null;
  //   }
  //   return {
  //     [CATEGORY]: { ...initialState, ...initialData },
  //   };
  // };

  const getTabs = () => {
    const tabs: FormTabProps[] = [];
    tabs.push(
      {
        key: "account",
        label: I18n.get.misc.account,
        fields: [
          {
            name: "email",
            label: I18n.get.user.labels.email,
            type: "text",
            required: true,
            grid: {
              xs: 12,
              md: 6,
            },
            validation: {
              required: I18n.get.form.errors.required,
              pattern: {
                value: regexPatterns.email,
                message: I18n.get.form.errors.invalidEmail,
              },
            },
            params: {
              inputProps: {
                "data-lpignore": true,
              },
            },
          },
          {
            name: "name",
            label: I18n.get.user.labels.name,
            type: "text",
            grid: {
              xs: 12,
              md: 3,
            },
            validation: {
              pattern: {
                value: regexPatterns.username,
                message: I18n.get.form.errors.invalidName,
              },
            },
            params: {
              inputProps: {
                "data-lpignore": true,
              },
            },
          },
          {
            name: "active",
            label: I18n.get.form.labels.active,
            type: "switch",
            required: true,
            grid: {
              xs: 12,
              md: 3,
            },
            params: {
              boolean: true,
              disabled: !isCreateNew() && (
                !canManageAccess || accessLoading || !!accessError || accountAccess?.user?.active !== true
              ),
              helperText: !isCreateNew() && accountAccess?.user?.active === false
                ? "Använd Återaktivera konto längre ner för att återaktivera kontot."
                : "",
            },
          },
          {
            name: "images",
            label: I18n.get.misc.image,
            type: "file_field",
            grid: {
              xs: 12,
            },
            params: {
              filetypes: ["image"],
              multiple: false,
              directUpload: true,
              portfolio: VoAuth.currentUser.settings.publicDefaultPortfolio,
              operations: {
                createFile: CREATE_FILE,
                updateFile: UPDATE_FILE,
                deleteFile: DELETE_FILE,
              },
            },
          },
          {
            name: "firstname",
            label: I18n.get.user.labels.firstname,
            type: "text",
            required: true,
            grid: {
              xs: 12,
              md: 6,
            },
            validation: {
              required: I18n.get.form.errors.required,
              pattern: {
                value: regexPatterns.stringNonDigit,
                message: I18n.get.form.errors.invalidFirstname,
              },
            },
            params: {
              inputProps: {
                "data-lpignore": true,
              },
            },
          },
          {
            name: "lastname",
            label: I18n.get.user.labels.lastname,
            type: "text",
            required: true,
            grid: {
              xs: 12,
              md: 6,
            },
            validation: {
              required: I18n.get.form.errors.required,
              pattern: {
                value: regexPatterns.stringNonDigit,
                message: I18n.get.form.errors.invalidLastname,
              },
            },
            params: {
              inputProps: {
                "data-lpignore": true,
              },
            },
          },
          {
            name: "apps",
            label: I18n.get.misc.apps,
            type: "checkboxes",
            grid: {
              xs: 12,
            },
            params: {
              availableValues: I18n.get.voadmin.apps,
              row: true,
            },
          },
          {
            name: "settings",
            label: I18n.get.misc.settings,
            type: "checkboxes",
            grid: {
              xs: 12,
            },
            params: {
              availableValues: I18n.get.user.settings,
            },
          },
        ],
      },
      {
        key: "personal_data",
        label: I18n.get.user.labels.personalData,
        fields: [
          {
            name: "profession",
            label: I18n.get.user.labels.professionalTitle,
            type: "text",
            grid: {
              xs: 12,
              md: 8,
            },
          },
          {
            name: "status",
            label: I18n.get.status.published,
            type: "switch",
            required: true,
            grid: {
              xs: 12,
              md: 4,
            },
          },
          {
            name: "personal_number",
            label: I18n.get.user.labels.personalNumber,
            type: "text",
            grid: {
              xs: 12,
            },
            validation: {
              validate: (value: any) => {
                if (!value || value === "") {
                  return true;
                }
                return (
                  !!value.match(regexPatterns.personalNumber) ||
                  I18n.get.form.errors.personalNumberInvalid
                );
              },
            },
          },
          {
            name: "address1",
            label: I18n.get.user.labels.address,
            type: "text",
            grid: {
              xs: 12,
              md: 6,
            },
          },
          {
            name: "address2",
            label: I18n.get.user.labels.address,
            type: "text",
            grid: {
              xs: 12,
              md: 6,
            },
          },
          {
            name: "postalcode",
            label: I18n.get.user.labels.postalcode,
            type: "text",
            grid: {
              xs: 12,
              md: 3,
            },
          },
          {
            name: "town",
            label: I18n.get.user.labels.town,
            type: "text",
            grid: {
              xs: 12,
              md: 5,
            },
          },
          {
            name: "country",
            label: I18n.get.user.labels.country,
            type: "text",
            grid: {
              xs: 12,
              md: 4,
            },
          },
          {
            name: "phone1",
            label: I18n.get.user.labels.phone,
            type: "text",
            grid: {
              xs: 12,
              md: 6,
            },
          },
          {
            name: "phone2",
            label: I18n.get.user.labels.phone,
            type: "text",
            grid: {
              xs: 12,
              md: 6,
            },
          },
          {
            name: "workplace",
            label: I18n.get.form.labels.workplace,
            type: "text",
            grid: {
              xs: 12,
            },
          },
        ],
      },
      {
        key: "roles",
        label: I18n.get.misc.roles,
        fields: [
          {
            name: state.group ? "groupRoles" : "roles",
            label: I18n.get.misc.roles,
            type: "content_list",
            grid: {
              xs: 12,
            },
            params: {
              dialog: {
                types: state.group ? ["group_role"] : ["role"],
              },
              renderItemTitle: (item: any) => item.title || item.label,
            },
          },
        ],
      }
    );

    if (state.group) {
      tabs[0].fields.push({
        name: "groups",
        label: "",
        type: "empty",
      });
    } else {
      tabs.push({
        key: "groups",
        label: I18n.get.group.labels.groups,
        fields: [
          {
            name: "groups",
            label: I18n.get.group.labels.groups,
            type: "content_list",
            grid: {
              xs: 12,
            },
            params: {
              dialog: {
                types: ["group"],
              },
            },
          },
        ],
      });
    }

    tabs[0].fields.push({
      name: "gdpr",
      label: "",
      type: "custom",
      grid: {
        xs: 12,
      },
      render: (data: any) => {
        if (!data) {
          return <></>;
        }
        return (
          <Typography
            variant="caption"
            fontStyle="italic"
            sx={{ alignItems: "center", display: "flex" }}
          >
            <CheckIcon fontSize="small" />
            &nbsp;{I18n.get.user.labels.gdpr}
          </Typography>
        );
      },
    });

    if (canManageAccess) {
      tabs[0].fields.push({
        name: "accountAccess",
        label: "",
        type: "custom",
        grid: { xs: 12 },
        render: () => (
          <AccountAccess
            id={String(urlParams?.id || params.id)}
            onComplete={(user) => handleRedirect(user, "complete")}
          />
        ),
      });
    }

    // tabs[0].fields.push({
    //   name: "gdpr",
    //   label: I18n.get.user.labels.gdpr,
    //   type: "switch",
    //   grid: {
    //     xs: 12,
    //   },
    //   params: {
    //     boolean: true,
    //     // disabled: !isCreateNew(),
    //   },
    // });

    if (isCreateNew()) {
      tabs[0].fields.push({
        name: "notify",
        label: I18n.get.user.labels.notifyUser,
        type: "switch",
        grid: {
          xs: 12,
        },
        params: {
          boolean: true,
        },
      });
    }

    return tabs;
  };

  const handleRedirect = (state: any, action: string) => {
    switch (action) {
      case "complete":
        if (onComplete) {
          onComplete(state);
          return;
        }
        break;
      case "cancel":
        if (onCancel) {
          onCancel(state);
          return;
        }
        break;
    }
    if (Utils.redirect(state)) {
      toast.info(I18n.get.form.messages.redirecting, { autoClose: 0 });
    } else {
      navigate("/users");
    }
  };

  const getContent = () => {
    return (
      <>
        <Form
          tabs={getTabs()}
          labels={{
            loading: I18n.get.form.labels.loadingContent,
            create: I18n.get.form.labels.createUser,
            created: I18n.get.user.messages.userCreated,
            updated: I18n.get.user.messages.userUpdated,
            deleted: I18n.get.user.messages.userDeleted,
            fieldRequired: I18n.get.form.errors.required,
          }}
          operations={{
            category: CATEGORY,
            get: GET_USER,
            create: CREATE_USER,
            update: UPDATE_USER,
          }}
          initialState={getInitialState()}
          // initialData={getInitialData()}
          pageTitleField="email"
          classes={{
            root: classes.root,
            // paper: state.group
            //   ? classes.content
            //   : "vo-global__content-with-toolbar",
            paper: "vo-global__content-with-toolbar",
            toolbar: "vo-global__content-toolbar",
            header: clsx("vo-global__content-with-toolbar", {
              [classes.header]: state.group,
            }),
          }}
          onComplete={(state: any) => handleRedirect(state, "complete")}
          onCancel={(state: any) => handleRedirect(state, "cancel")}
          urlParams={urlParams}
          header={
            state.group &&
            isCreateNew() && (
              <Alert severity="warning">
                {I18n.get.group.messages.userAlreadyExistsGetInTouch}
              </Alert>
            )
          }
        />
      </>
    );
  };

  // Effects.

  // useEffect(() => {
  //   let currentGroup = VoGroups.getCurrent();
  //   setGroup(currentGroup);
  // }, []);

  if (dialog) {
    return <>{getContent()}</>;
  }

  return <Layout>{getContent()}</Layout>;
};

export default UserEdit;
